import React, { useState } from 'react';
import styles from './index.module.scss';
import { formatContentHTML } from '../../../../../../utils/formatBoldText';
import ImageModal from '../../../../img_components/publication_card_image_v1/ImageModal';

const normalizeText = (value) => {
  const text = String(value ?? '').trim();
  if (!text || text.toLowerCase() === 'none') return '';
  return text;
};

const isTextLikeType = (type) => type === 'text' || type === 'long_text' || type === 'long text';

const getContentType = (item) => String(item?.content_type || item?.cotent_type || '').toLowerCase();

const getHeadingLevel = (item) => {
  const raw = item?.H1_H6_data ?? item?.h1_h6_data;
  if (raw == null || raw === '' || String(raw).toLowerCase() === 'none') return null;
  const parsed = parseInt(String(raw), 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const getImageFileName = (value) => {
  const rawName = normalizeText(value);
  if (!rawName) return '';
  if (rawName.includes('\\')) return rawName.substring(rawName.lastIndexOf('\\') + 1);
  if (rawName.includes('/')) return rawName.split('/').pop();
  return rawName;
};

const buildMediaUrl = (pathToFolder, fileName) => {
  if (!pathToFolder || !fileName) return '';
  const base = pathToFolder.startsWith('http') ? pathToFolder : `https://${pathToFolder}`;
  return `${base}${fileName}`;
};

const parseImgsNames = (rawValue) => {
  if (!rawValue || rawValue === 'none' || rawValue === '[]') return [];

  if (Array.isArray(rawValue)) {
    return rawValue.map((name) => getImageFileName(name)).filter(Boolean);
  }

  if (typeof rawValue === 'string') {
    try {
      const parsed = JSON.parse(rawValue);
      if (Array.isArray(parsed)) {
        return parsed.map((name) => getImageFileName(name)).filter(Boolean);
      }
    } catch {
      // fall through
    }

    return rawValue
      .split(',')
      .map((name) => getImageFileName(name.replace(/['"[\]]/g, '')))
      .filter(Boolean);
  }

  return [];
};

const renderFormattedText = (text, className, Tag = 'div') => {
  const normalized = normalizeText(text);
  if (!normalized) return null;

  return (
    <Tag
      className={className}
      dangerouslySetInnerHTML={{ __html: formatContentHTML(normalized) }}
    />
  );
};

const renderBodyText = (text, keyPrefix) =>
  text.split('\n').map((line, lineIndex) => (
    <p
      key={`${keyPrefix}-line-${lineIndex}`}
      className={styles.textLine}
      dangerouslySetInnerHTML={{ __html: formatContentHTML(line) }}
    />
  ));

const ArticleFreeContentBlock = ({ blockData, path_to_folder }) => {
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState('');
  const [modalImageAlt, setModalImageAlt] = useState('');

  const openImageModal = (imageSrc, imageAlt = '') => {
    if (!imageSrc) return;
    setModalImageSrc(imageSrc);
    setModalImageAlt(imageAlt);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
  };

  const renderTextLikeContent = (item, index) => {
    const text = normalizeText(item?.content_text);
    if (!text) return null;

    const headingLevel = getHeadingLevel(item);
    if (headingLevel === 1) {
      return renderFormattedText(text, styles.mainTitle, 'h1');
    }
    if (headingLevel === 2) {
      return renderFormattedText(text, styles.subtitle, 'h2');
    }

    return renderBodyText(text, `content-${index}`);
  };

  const renderContent = (item, index) => {
    const type = getContentType(item);

    if (isTextLikeType(type)) {
      const rendered = renderTextLikeContent(item, index);
      if (!rendered) return null;

      return (
        <div key={`content-${index}`} className={styles.contentItem}>
          {rendered}
        </div>
      );
    }

    if (type === 'img') {
      const fileName = getImageFileName(item?.img_name);
      if (!fileName) return null;

      const imageSrc = buildMediaUrl(path_to_folder, fileName);
      const caption = normalizeText(item?.content_text);

      return (
        <figure key={`content-${index}`} className={styles.figure}>
          <img
            src={imageSrc}
            alt={caption || ''}
            className={styles.image}
            loading='lazy'
            onClick={() => openImageModal(imageSrc, caption || '')}
          />
          {caption && (
            <figcaption
              className={styles.caption}
              dangerouslySetInnerHTML={{ __html: formatContentHTML(caption) }}
            />
          )}
        </figure>
      );
    }

    if (type === 'imgs_block') {
      const images = parseImgsNames(item?.imgs_names);
      if (images.length === 0) return null;

      const caption = normalizeText(item?.content_text);

      return (
        <figure key={`content-${index}`} className={styles.galleryFigure}>
          <div className={styles.galleryGrid}>
            {images.map((name, imageIndex) => {
              const gallerySrc = buildMediaUrl(path_to_folder, name);

              return (
                <img
                  key={`gallery-${index}-${imageIndex}`}
                  src={gallerySrc}
                  alt={caption || ''}
                  className={styles.galleryImage}
                  loading='lazy'
                  onClick={() => openImageModal(gallerySrc, caption || '')}
                />
              );
            })}
          </div>
          {caption && (
            <figcaption
              className={styles.caption}
              dangerouslySetInnerHTML={{ __html: formatContentHTML(caption) }}
            />
          )}
        </figure>
      );
    }

    if (type === 'video') {
      const videoName = getImageFileName(item?.video_name);
      const posterName = getImageFileName(item?.img_name);
      if (!videoName) return null;

      const videoSrc = buildMediaUrl(path_to_folder, videoName);
      const posterSrc = posterName ? buildMediaUrl(path_to_folder, posterName) : undefined;

      return (
        <div key={`content-${index}`} className={styles.videoWrap}>
          <video src={videoSrc} poster={posterSrc} controls className={styles.video} />
        </div>
      );
    }

    if (type === 'audio') {
      const audioName = getImageFileName(item?.audio_name);
      if (!audioName) return null;

      return (
        <div key={`content-${index}`} className={styles.audioWrap}>
          <audio src={buildMediaUrl(path_to_folder, audioName)} controls className={styles.audio} />
        </div>
      );
    }

    const text = normalizeText(item?.content_text);
    if (!text) return null;

    return (
      <div key={`content-${index}`} className={styles.contentItem}>
        {renderBodyText(text, `fallback-${index}`)}
      </div>
    );
  };

  if (!blockData?.block_content || !Array.isArray(blockData.block_content)) {
    return null;
  }

  const renderedItems = blockData.block_content.map(renderContent).filter(Boolean);
  if (renderedItems.length === 0) return null;

  return (
    <>
      <section className={styles.articleFreeContentBlock}>{renderedItems}</section>
      <ImageModal
        isOpen={isImageModalOpen}
        onClose={closeImageModal}
        imageSrc={modalImageSrc}
        imageAlt={modalImageAlt}
      />
    </>
  );
};

export default ArticleFreeContentBlock;
